/** What a deletion takes with it. One kind per row the dialog can draw. */
export enum DeleteLossKind {
  /** Sign-ups the campaign owes fans and will never deliver. */
  presaves = 'presaves',
  /** The address stops answering, wherever it has been printed. */
  link = 'link',
  analytics = 'analytics',
  emails = 'emails',
}

export interface DeleteLossRow {
  kind: DeleteLossKind;
  /** A loss that lands on someone else, or that no export can undo. */
  isHard: boolean;
  /** The count printed beside the row. Null when the row is not a number. */
  figure: number | null;
}

export interface DeleteLossFacts {
  isPublished: boolean;
  shareUrl: string | null;
  /** Sign-ups a scheduled campaign still owes. Null when there is no campaign. */
  pendingPresaveCount: number | null;
  /** Views over the stats window. Null while unknown. */
  viewCount: number | null;
  /** Addresses collected by this page's campaign. Null when there is no campaign. */
  emailCount: number | null;
}

/**
 * The list under "What goes with it".
 *
 * A row exists only when the thing it names exists: no campaign, no cancelled
 * pre-saves; no traffic, no analytics to lose. An empty list is the answer to
 * "is anything at stake here" — the draft dialog asks for nothing because
 * there is nothing on this list.
 */
export function deleteLossRows(facts: DeleteLossFacts): DeleteLossRow[] {
  const rows: DeleteLossRow[] = [];

  if (facts.pendingPresaveCount !== null && facts.pendingPresaveCount > 0) {
    rows.push({ kind: DeleteLossKind.presaves, isHard: true, figure: facts.pendingPresaveCount });
  }
  if (facts.isPublished && facts.shareUrl !== null) {
    rows.push({ kind: DeleteLossKind.link, isHard: true, figure: null });
  }
  if (facts.viewCount !== null && facts.viewCount > 0) {
    rows.push({ kind: DeleteLossKind.analytics, isHard: false, figure: facts.viewCount });
  }
  if (facts.emailCount !== null && facts.emailCount > 0) {
    rows.push({ kind: DeleteLossKind.emails, isHard: false, figure: facts.emailCount });
  }

  return rows;
}

/** True when the typed name and the page's name are the same string to a reader. */
export function isConfirmedName(typed: string, name: string): boolean {
  return typed.trim().toLocaleLowerCase() === name.trim().toLocaleLowerCase() && name.trim() !== '';
}
